import type { LatLng } from '../../shared/types/index.js';

/** Where the map should fly next: a point (with optional zoom) or a bounding box. */
export type FlyTarget =
  | { lat: number; lng: number; zoom?: number; bounds?: undefined }
  | { bounds: [[number, number], [number, number]]; lat?: undefined; lng?: undefined; zoom?: undefined };

/** Great-circle distance in metres. */
export function haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const f1 = (lat1 * Math.PI) / 180,
    f2 = (lat2 * Math.PI) / 180;
  const df = ((lat2 - lat1) * Math.PI) / 180,
    dl = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(df / 2) ** 2 + Math.cos(f1) * Math.cos(f2) * Math.sin(dl / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Bounding box around the given points, padded by `bufferM` metres, or null when there are none. */
export function fitToSites(positions: ReadonlyArray<LatLng>, bufferM = 1000): FlyTarget | null {
  const pts = positions.filter((p) => p.lat != null && p.lng != null);
  if (!pts.length) return null;

  const lats = pts.map((p) => p.lat);
  const lngs = pts.map((p) => p.lng);

  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);

  // 1° latitude ≈ 111,320 m; longitude shrinks by cos(lat)
  const midLat = (minLat + maxLat) / 2;
  const dLat = bufferM / 111320;
  const dLng = bufferM / (111320 * Math.cos((midLat * Math.PI) / 180));

  return {
    bounds: [
      [minLat - dLat, minLng - dLng],
      [maxLat + dLat, maxLng + dLng],
    ],
  };
}
